"use client";

import React from "react";
import { defaultRenderImage } from "../../../utils/image/renderImage";
import {
  CardBody,
  CardDescription,
  CardMedia,
  CardRoot,
  CardTitle,
  DetailsLink,
  OverlayShade,
  OverlaySpacer,
} from "../DynamicHighlights.styles";
import type { BentoSpan } from "../bentoLayout";
import {
  HIGHLIGHT_ASPECT,
  HIGHLIGHT_SINGLE_ASPECT,
  HIGHLIGHT_WIDE_ASPECT,
  type DynamicHighlightsProps,
  type HighlightsFormat,
  type HighlightsCardHeight,
  type HighlightsDetailsVariant,
  type HighlightsImageAspect,
  type HighlightsTextTag,
  type HighlightItem,
} from "../types";
import { DEFAULT_DETAILS_LABEL } from "../strings";

export type HighlightCardColors = {
  cardBgColor?: string;
  cardBorderColor?: string;
  itemTitleColor?: string;
  itemDescriptionColor?: string;
  detailsLinkColor?: string;
  overlayColor?: string;
};

export type HighlightCardProps = {
  item: HighlightItem;
  format: HighlightsFormat;
  colors: HighlightCardColors;
  showTitle: boolean;
  showDescription: boolean;
  itemTitleTag: HighlightsTextTag;
  itemDescriptionTag: HighlightsTextTag;
  /** O item abre modal (tem descrição completa, modal ligado e não é builder). */
  clickable: boolean;
  detailsLabel?: string;
  /** Estilo do botão de detalhes. */
  detailsVariant?: HighlightsDetailsVariant;
  onOpen?: (id: string) => void;
  renderImage?: DynamicHighlightsProps["renderImage"];
  /**
   * Célula que o card ocupa no bento. `minor` é o card normal (também o valor
   * da grade regular e do carrossel); as células largas mudam a proporção e,
   * quando o texto fica fora da imagem, a direção do card.
   */
  span?: BentoSpan;
  /** `auto` respeita a proporção original da imagem (só no formato `below`). */
  imageAspect?: HighlightsImageAspect;
  /** Altura do card no desktop. */
  cardHeight?: HighlightsCardHeight;
  /** Altura do card no mobile — escala própria (ver HIGHLIGHT_ASPECT). */
  cardHeightMobile?: HighlightsCardHeight;
  /** Um item por página no carrossel: o card ocupa a coluna e pede proporção larga. */
  single?: boolean;
  /** Medido pelo container da seção (nunca matchMedia) — decide a escala de proporção. */
  isMobile?: boolean;
};

const DEFAULT_OVERLAY_COLOR = "#00000099";

export const HighlightCard = ({
  item,
  format,
  colors,
  showTitle,
  showDescription,
  itemTitleTag,
  itemDescriptionTag,
  clickable,
  detailsLabel,
  detailsVariant = "link",
  onOpen,
  renderImage = defaultRenderImage,
  span = "minor",
  imageAspect = "fixed",
  cardHeight = "medium",
  cardHeightMobile = "medium",
  single = false,
  isMobile = false,
}: HighlightCardProps) => {
  const overlay = format === "overlay";
  const aspect = HIGHLIGHT_ASPECT[format];
  /*
   * A proporção da mídia acompanha a célula:
   *
   * - `major`/`full` são as células largas: no formato sobreposto a mídia fica
   *   panorâmica; com o texto fora o card vira LADO A LADO (imagem à esquerda,
   *   texto à direita) e quem manda na altura é a linha.
   * - `minor` usa a proporção do formato.
   */
  const sideBySide = !overlay && (span === "major" || span === "full");

  /*
   * "Auto" respeita a proporção original da imagem — só vale no formato
   * `below`, onde a mídia manda na altura do card. No `overlay` a imagem É o
   * fundo do card, então não existe altura para ela ditar: ali a proporção é
   * sempre a do formato.
   */
  const freeAspect = imageAspect === "auto" && !overlay && !sideBySide;

  /*
   * A proporção é resolvida AQUI, não numa media query.
   *
   * O `isMobile` vem do `useContainerIsMobile` da seção, que mede o próprio
   * bloco — é o que funciona no preview mobile do builder, onde a viewport do
   * browser não muda. Com a media query de 767px, trocar a altura no mobile não
   * surtia efeito nenhum ali.
   */
  const resolvedAspect = (() => {
    if (sideBySide || freeAspect) return "auto";
    if (isMobile) return aspect.mobile[cardHeightMobile];
    // Célula larga do bento: escala própria, mas que ACOMPANHA a altura escolhida
    // (antes era 21/9 fixo, e mudar a altura não fazia nada no bento).
    if (span === "major" || span === "full") return HIGHLIGHT_WIDE_ASPECT[cardHeight];
    // Card sozinho no slide: a proporção de retrato deixaria o card altíssimo.
    if (single) return HIGHLIGHT_SINGLE_ASPECT[cardHeight];
    return aspect.desktop[cardHeight];
  })();

  const handleOpen = () => {
    if (!clickable) return;
    onOpen?.(item.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!clickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen?.(item.id);
    }
  };

  const media = (
    <CardMedia
      $format={format}
      $aspect={resolvedAspect}
      $sideBySide={sideBySide}
    >
      {renderImage({ src: item.image, alt: item.title })}
      {overlay && (
        <OverlayShade $color={colors.overlayColor || DEFAULT_OVERLAY_COLOR} />
      )}
    </CardMedia>
  );

  return (
    <CardRoot
      $format={format}
      $bg={colors.cardBgColor}
      $border={colors.cardBorderColor}
      $clickable={clickable}
      $sideBySide={sideBySide}
      data-testid="dynamic-highlights-card"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
    >
      {media}
      {/* Sobreposto: a mídia é absoluta, então a altura vem deste espaçador. */}
      {overlay && (
        <OverlaySpacer
          $aspect={resolvedAspect}
        />
      )}
      <CardBody $format={format} $sideBySide={sideBySide}>
        {/*
          `as` troca o elemento sem mexer no estilo: a tipografia do card é
          sempre a mesma (Title/Text do styleguide), o que muda é só a tag, que
          existe para a página manter a hierarquia de headings.
        */}
        {showTitle && !!item.title && (
          <CardTitle as={itemTitleTag} $color={colors.itemTitleColor} $overlay={overlay}>
            {item.title}
          </CardTitle>
        )}
        {showDescription && !!item.description && (
          <CardDescription
            as={itemDescriptionTag}
            $color={colors.itemDescriptionColor}
            $overlay={overlay}
          >
            {item.description}
          </CardDescription>
        )}
        {clickable && (
          <DetailsLink
            $color={colors.detailsLinkColor}
            $overlay={overlay}
            $variant={detailsVariant}
          >
            {detailsLabel?.trim() || DEFAULT_DETAILS_LABEL}
          </DetailsLink>
        )}
      </CardBody>
    </CardRoot>
  );
};
